import React, { useEffect, useState, useContext, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CSDataTable from "../../components/CSDataTable";
import { CSDataTableColumns } from "../../modal/CSDataTable";
import { convertToDateMonthFormat } from "../../utils/ConvertDate";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { DisplayRiskManagementPlanDetails } from "./Component/DisplayRiskManagementPlanDetails";
import { RiskManagementplanList } from "./modal";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import { useRiskManagementContext } from "../../context/RiskManagement/RiskManagementContext";
import { useUserContext } from "../../context/auth/UserContext";
import { ListItem, Tooltip } from "@mui/material";
import { CircularProgress } from "@mui/material";
import {
  fetchRiskManagementList,
  fetchRiskManagementTemplate,
  createRiskManagementPlan,
  updateRiskManagementPlan,
  fetchRiskManagementPlanDetails,
} from "./helper";
import ImportDialogBox from "../../components/ImportDialogBox";
import DuplicateDialogBox from "../../components/DuplicateDialogBox";
import { useAccessRights } from "../../utils/useAccessRights";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2, pb: 1 }}>{children}</Box>}
    </div>
  );
}

export const RiskManagement = () => {
  const navigate = useNavigate();
  const riskManagementPageAccess = useAccessRights("Risk Management - CS & SC");

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [csPlanList, setCsPlanList] = useState<RiskManagementplanList[]>([]);
  const [scPlanList, setScPlanList] = useState<RiskManagementplanList[]>([]);
  const [importTemplates, setImportTemplates] = useState<any[]>([]);
  const [latestVersion, setLatestVersion] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = React.useState(0);
  const [dialogImportState, setDialogImportState] = useState({
    title: "",
    body: "",
    isBtnsDisabled: false,
    openImportDialog: false,
  });
  const [dialogDuplicateState, setDialogDuplicateState] = useState({
    title: "",
    body: "",
    isBtnsDisabled: false,
    openDuplicateDialog: false,
  });
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openDuplicateDialog: false,
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const {
    selectedRiskManagement,
    setSelectedRiskManagement,
    tabValue,
    setTabValue,
    setPlanDetails,
  } = useRiskManagementContext();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelectRow = (selectedRows: RiskManagementplanList[]) => {
    setSelectedRiskManagement(selectedRows[0]);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleCloseMenu = (val: string | null) => {
    setAnchorEl(null);
    if (val === "Import") {
      displayImportConfirm();
    } else if (val === "Appendix") {
      if (
        selectedRiskManagement?.csRiskManagementPlanID ||
        selectedRiskManagement?.supplyChainPlanID
      ) {
        navigate("/risk-management-appendix");
      } else {
        setDialogState({
          title: "Info",
          body: "Please select a plan",
          action: "OK",
          actionCancel: "",
          openDuplicateDialog: false,
          openSuccessDialogBox: true,
        });
      }
    } else if (val === "duplicate") {
      displayDuplicateConfirm();
    }
  };

  const handleDialogCancel = () => {
    setDialogState({
      ...dialogState,
      openDuplicateDialog: false,
      openSuccessDialogBox: false,
    });
    setDialogImportState({
      ...dialogState,
      isBtnsDisabled: false,
      openImportDialog: false,
    });
    setDialogDuplicateState({
      ...dialogState,
      isBtnsDisabled: false,
      openDuplicateDialog: false,
    });
  };

  const displayDuplicateConfirm = () => {
    const list = value === 0 ? csPlanList : scPlanList;
    const _latestVersion = list.sort(
      (a: any, b: any) => parseFloat(b.version) - parseFloat(a.version)
    )[0];
    // console.log("latestVersion", _latestVersion)
    setLatestVersion(_latestVersion);
    const planType = value === 0 ? "Cybersecurity" : "Supply Chain";
    setDialogDuplicateState({
      title: `Duplicate ${planType} Risk Management Plan`,
      body: `Are you sure you want to duplicate lastest version <strong>${_latestVersion.version}</strong> of ${planType} Risk Management Plan?`,
      openDuplicateDialog: true,
      isBtnsDisabled: false,
    });
  };

  const duplicatePlan = async () => {
    setDialogDuplicateState({
      ...dialogDuplicateState,
      isBtnsDisabled: true,
    });
    const latestVersionDetails = await fetchRiskManagementPlanDetails(
      value === 0 ? "cyberSecurity" : "supplyChain",
      value === 0
        ? latestVersion?.csRiskManagementPlanID
        : latestVersion?.supplyChainPlanID
    );
    if (
      latestVersionDetails?.csRiskManagementPlanID ||
      latestVersion?.supplyChainPlanID
    ) {
      const date = new Date();
      const payload = {
        ...(value === 0 && {
          prevCSRiskManagementPlanID:
            latestVersionDetails.csRiskManagementPlanID,
        }),
        ...(value === 1 && {
          prevSupplyChainPlanID: latestVersionDetails.supplyChainPlanID,
        }),
        overview: latestVersionDetails.overview,
        purpose: latestVersionDetails.purpose,
        scope: latestVersionDetails.scope,
        definition: latestVersionDetails.definition,
        plan: latestVersionDetails.plan,
        compliance: latestVersionDetails.compliance,
        relatedPolicies: latestVersionDetails.relatedPolicies,
        version: latestVersionDetails.version,
        dateApproved: null,
        status: "Not Approved",
        createDate: date,
        companyProfileID: companyProfileID,
        createdBy: userId,
        approvedBy: 0,
        isDuplicate: true,
      };
      const res = await createRiskManagementPlan(
        value === 0 ? "cyberSecurity" : "supplyChain",
        payload
      );
      if (res.succeeded) {
        handleDialogCancel();
        fetchTableData();
        setDialogState({
          title: "Success",
          body: "Plan successfully added",
          action: "OK",
          actionCancel: "",
          openDuplicateDialog: false,
          openSuccessDialogBox: true,
        });
        setDialogDuplicateState({
          ...dialogDuplicateState,
          isBtnsDisabled: false,
        });
      }
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDuplicateDialog: false,
        openSuccessDialogBox: true,
      });
      setDialogDuplicateState({
        ...dialogDuplicateState,
        isBtnsDisabled: false,
      });
    }
  };

  const displayImportConfirm = async () => {
    const planType = value === 0 ? "Cybersecurity" : "Supply Chain";
    const riskManagementPlanTemplate = await fetchRiskManagementTemplate(
      value === 0
    );
    setImportTemplates(riskManagementPlanTemplate.data);
    // console.log("riskManagementPlanTemplate",riskManagementPlanTemplate)
    setDialogImportState({

      title: `Import ${planType} Risk Management Plan`,
      body: `By importing the template plan, you will overwrite the content in any existing version 1.0 plan for your organization.`,

      isBtnsDisabled: false,
      openImportDialog: true,
    });
  };

  const handleImportPlan = async () => {
    setDialogImportState({
      ...dialogImportState,
      isBtnsDisabled: true,
    });

    const list = value === 0 ? csPlanList : scPlanList;
    const isExsistingPlan = list.filter(
      (ele) => ele.version === importTemplates[0].version
    );
    // console.log("isExsistingPlan", isExsistingPlan);
    let hasError = false;
    const date = new Date();

    if (isExsistingPlan.length > 0) {
      const payload = {
        overview: importTemplates[0].overview,
        purpose: importTemplates[0].purpose,
        scope: importTemplates[0].scope,
        definition: importTemplates[0].definitions,
        plan: importTemplates[0].plan,
        compliance: importTemplates[0].compliance,
        relatedPolicies: importTemplates[0].relatedDocuments,
        version: importTemplates[0].version,
        dateApproved: null,
        status: "Not Approved",
        updateDate: date,
        updateBy: userId,
        companyProfileID: companyProfileID,
        approvedBy: 0,
        ...(value === 0
          ? {
              csRiskManagementPlanID: isExsistingPlan[0].csRiskManagementPlanID,
            }
          : { supplyChainPlanID: isExsistingPlan[0].supplyChainPlanID }),
      };

      const resUpdate = await updateRiskManagementPlan(
        value === 0 ? "cyberSecurity" : "supplyChain",
        payload
      );
      // console.log("resUpdate",resUpdate)
      if (!resUpdate.succeeded) {
        hasError = true;
      }
    } else {
      const payload = {
        overview: importTemplates[0].overview,
        purpose: importTemplates[0].purpose,
        scope: importTemplates[0].scope,
        definition: importTemplates[0].definitions,
        plan: importTemplates[0].plan,
        compliance: importTemplates[0].compliance,
        relatedPolicies: importTemplates[0].relatedDocuments,
        version: importTemplates[0].version,
        dateApproved: null,
        status: "Not Approved",
        createDate: date,
        companyProfileID: companyProfileID,
        createdBy: userId,
        approvedBy: 0,
      };
      const resImport = await createRiskManagementPlan(
        value === 0 ? "cyberSecurity" : "supplyChain",
        payload
      );
      // console.log("resImport",resImport)
      if (!resImport.succeeded) {
        hasError = true;
      }
    }

    if (hasError) {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDuplicateDialog: false,
        openSuccessDialogBox: true,
      });
      setDialogImportState({
        ...dialogImportState,
        isBtnsDisabled: false,
      });
    } else {
      fetchTableData();
      setPlanDetails({});
      setDialogState({
        title: "Success",
        body: "Plan successfully imported",
        action: "OK",
        actionCancel: "",
        openDuplicateDialog: false,
        openSuccessDialogBox: true,
      });
      setDialogImportState({
        ...dialogImportState,
        isBtnsDisabled: false,
      });
    }
  };

  const fetchTableData = async () => {
    setIsLoading(true);
    const planList = await fetchRiskManagementList(
      value === 0 ? "cyberSecurity" : "supplyChain",
      companyProfileID
    );
    const formattedList = planList.map((plan: RiskManagementplanList) => ({
      ...plan,
      rowId: value === 0 ? plan.csRiskManagementPlanID : plan.supplyChainPlanID,
      approvedDate: convertToDateMonthFormat(plan.dateApproved),
      createdDate: convertToDateMonthFormat(plan.createDate),
    }));
    value === 0 ? setCsPlanList(formattedList) : setScPlanList(formattedList);
    setIsLoading(false);
    setSelectedRiskManagement(null);
    setPlanDetails({});
  };

  const columns: CSDataTableColumns[] = [
    {
      accessorKey: "version",
      header: "Version",
      size: "200px",
    },
    {
      accessorKey: "status",
      header: "Status",
      size: "60px",
    },
    {
      accessorKey: "createdDate",
      header: "Created Date",
      size: "80px",
    },
    {
      accessorKey: "approvedDate",
      header: "Approved Date",
      size: "80px",
    },
  ];

  useEffect(() => {
    setTabValue(value);
    fetchTableData();
    setPlanDetails({});
    setSelectedRiskManagement(null);
  }, [value]);

  useEffect(() => {
    setSelectedRiskManagement(null);
    setPlanDetails({});
    if (tabValue) {
      setValue(tabValue);
    }
  }, []);

  return (
    <Fragment>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        <Grid
          xs={6}
          style={{
            padding: "1rem",
            margin: "1rem 0",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid xs={3}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                sx={{
                  ".MuiTab-root": {
                    textTransform: "none",
                    borderRadius: 1,
                    margin: 0.5,
                    padding: "2px 10px",
                    minHeight: "34px",
                    fontSize: "0.875rem",
                    "&.Mui-selected": {
                      backgroundColor: "#0066FF",
                      color: "white",
                    },
                  },
                  ".MuiTabs-indicator": {
                    display: "none",
                  },
                }}
                TabIndicatorProps={{ sx: { display: "none" } }}
              >
                <Tab label="Cybersecurity Risk Management" />
                <Tab label="Supply Chain Risk Management" />
              </Tabs>
            </Grid>
            <Grid>
              <Tooltip title="Menu">
                <IconButton
                  aria-label="menu"
                  disabled={isLoading}
                  onClick={handleClick}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleCloseMenu(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  disabled={!riskManagementPageAccess?.isImport}
                  onClick={() => handleCloseMenu("Import")}
                >
                  Import
                </MenuItem>
                <MenuItem onClick={() => handleCloseMenu("Appendix")}>
                  Appendix
                </MenuItem>
                <MenuItem
                  disabled={!riskManagementPageAccess?.isCreate}
                  onClick={() => handleCloseMenu("duplicate")}
                >
                  Duplicate Plan
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
          <CustomTabPanel value={value} index={0}>
            {isLoading ? (
              <ListItem
                sx={{ width: "100%", display: "flex", flexDirection: "column" }}
              >
                <CircularProgress />
              </ListItem>
            ) : (
              <CSDataTable
                rows={csPlanList}
                columns={columns}
                // previewRecord={csPlanList[0]}
                multiRowSelection={false}
                handleSelectRow={(selectedRows) =>
                  handleSelectRow(selectedRows)
                }
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {isLoading ? (
              <ListItem
                sx={{ width: "100%", display: "flex", flexDirection: "column" }}
              >
                <CircularProgress />
              </ListItem>
            ) : (
              <CSDataTable
                rows={scPlanList}
                columns={columns}
                // previewRecord={scPlanList[0]}
                multiRowSelection={false}
                handleSelectRow={(selectedRows) =>
                  handleSelectRow(selectedRows)
                }
              />
            )}
          </CustomTabPanel>
        </Grid>
        <Grid
          xs
          style={{
            padding: "1rem",
            margin: "1rem",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <DisplayRiskManagementPlanDetails
            loadingPage={isLoading}
            refreshList={() => fetchTableData()}
          />
        </Grid>
      </Grid>
      {
        <ImportDialogBox
          openDialogBox={dialogImportState.openImportDialog}
          dialogTitle={dialogImportState.title}
          dialogBody={dialogImportState.body}
          isBtnsDisabled={dialogImportState.isBtnsDisabled}
          handleFunction={handleImportPlan}
          handleCancel={handleDialogCancel}
        />
      }
      {
        <DuplicateDialogBox
          openDialogBox={dialogDuplicateState.openDuplicateDialog}
          dialogTitle={dialogDuplicateState.title}
          dialogBody={dialogDuplicateState.body}
          isBtnsDisabled={dialogDuplicateState.isBtnsDisabled}
          handleFunction={duplicatePlan}
          handleCancel={handleDialogCancel}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleDialogCancel}
        />
      }
    </Fragment>
  );
};
